import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { Variant } from '@ap/amounts';
import { ApWindowService } from '@ap/cdk';
import { ApContentService, ContentPrefix } from '@ap/content';
import { ApHeaderComponent, NavigationItem, NavigationType, ProfileMenu, ProfileMenuItem } from '@ap/header';
import { KlantbeheerResource, KlantDetails } from '@ap/interface-apic-klantbeheer';
import { ContractDetails } from '@ap/interface-apix-mijn-overzicht';
import { MijnConditiesService } from '@ap/mijn-condities';
import { MijnFeedbackSettings, Variants } from '@ap/mijn-feedback';
import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';
import { MijnNavigatieService, MijnRouteKey } from '@ap/mijn-navigatie';
import { MijnZoekenComponent, ResultItem, SearchOptions, SearchVariant } from '@ap/mijn-zoeken';
import { ApModalComponent, ApModalService, ModalClosedReason, ModalOptions, ModalSize } from '@ap/modal';
import { ApPersistenceService } from '@ap/persistence';
import { DEFAULT_BREAKPOINT_SMALL } from '@ap/style';

import content from '../mijn-template.content.json';
import { MijnTemplateNavigation } from '../mijn-template.navigation';
import { MijnTemplateService, XC_HEADLESS_CONTENT } from '../mijn-template.service';

import { Breadcrumb } from './breadcrumb/breadcrumb.model';
import { ChatNotification } from './conversational/conversational.model';
import { CONVERSATIONAL_CONFIGURATION_NAME, MAX_BUTTON_TEXTLENGTH_FOR_ROW_LAYOUT } from './mijn-overzicht-template.constant';
import { FeedbackConfiguration, MijnFeedbackForm } from './mijn-overzicht-template.model';
import { MijnOverzichtTemplateService } from './mijn-overzicht-template.service';
import { SendableOrdersModalComponent } from './sendable-orders-modal/sendable-orders-modal.component';

/**
 * @module MijnOverzichtTemplateModule
 * @ngdoc component
 * @name MijnOverzichtTemplateComponent
 */
@Component({
    selector: 'mijn-overzicht-template',
    templateUrl: './mijn-overzicht-template.component.html',
    styleUrls: ['./mijn-overzicht-template.component.scss'],
    standalone: false
})
export class MijnOverzichtTemplateComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ViewChild(ApHeaderComponent)
    public header: ApHeaderComponent;

    @Input()
    public title: string;

    @Input()
    public tabTitle: string;

    @Input()
    public breadcrumb: Breadcrumb;

    @Input()
    public subtitle?: string;

    @Input()
    public secondarySubtitle?: string;

    @Input()
    public amount?: number;

    @Input()
    public amountVariant: Variant = Variant.NEGATIVE_ACCENT;

    @Input()
    public amountContrast = false;

    @Input()
    public secondarySubAmount?: number;

    @Input()
    public primaryActionLabel?: string;

    @Input()
    public secondaryActionLabel?: string;

    @Input()
    public variant: 'primary' | 'secondary' = 'primary';

    @Input()
    public loading: boolean;

    @Input()
    public avatar?: string;

    @Input()
    public changeConversationalStatus$: Subject<string>;

    /**
     * Updates the dynamic breadcrumb and should be used when a router-outlet is used inside the template
     */
    @Input()
    public updateURL?: string;

    @Input()
    public feedbackConfiguration?: FeedbackConfiguration;

    @Input()
    public updateUnreadMessages$?: Subject<void>;

    @Input()
    public updatePendingActions$?: Subject<void>;

    @Input()
    public showHeaderItems: boolean = true;

    @Output()
    public primaryActionClicked = new EventEmitter<void>();

    @Output()
    public secondaryActionClicked = new EventEmitter<void>();

    @Output()
    public chatNotificationSent = new EventEmitter<ChatNotification>();

    @ViewChild('skipLink', { read: ElementRef }) skipLinkElement?: ElementRef;

    private readonly contentService: ApContentService = inject(ApContentService);
    private readonly navigation: MijnTemplateNavigation = inject(MijnTemplateNavigation);
    private readonly launchdarklyService: MijnLaunchDarklyService = inject(MijnLaunchDarklyService);
    private readonly mijnNavigatieService: MijnNavigatieService = inject(MijnNavigatieService);
    private readonly mijnOverzichtService: MijnOverzichtTemplateService = inject(MijnOverzichtTemplateService);
    private readonly mijnTemplateService: MijnTemplateService = inject(MijnTemplateService);
    private readonly modalService: ApModalService = inject(ApModalService);
    private readonly persistenceService: ApPersistenceService = inject(ApPersistenceService);
    private readonly renderer: Renderer2 = inject(Renderer2);
    private readonly windowService: ApWindowService = inject(ApWindowService);
    private readonly klantBeheerResource: KlantbeheerResource = inject(KlantbeheerResource);
    private readonly document: Document = inject(DOCUMENT);
    private readonly browserTabTitle: Title = inject(Title);
    private readonly mijnConditiesService: MijnConditiesService = inject(MijnConditiesService);

    public navigationItems: NavigationItem[] = [];
    public profileMenu: ProfileMenu;
    public logoLink: string;
    public secondaryClassList: string;
    public dynamicBreadcrumb: Breadcrumb;
    public conversationalEnabled: boolean;
    public reload = false;
    public buttonsInColumns = false;
    public isMobileSize: boolean;
    public isKeyboardNavigation: boolean;

    public usabillaFormSettings: MijnFeedbackForm = null;
    public skipLinkTarget: HTMLElement;

    private modalComponent: ApModalComponent;
    private unreadMessagesSize = 0;
    private pendingActionsCount = 0;
    private userName = null;
    private companyName = null;
    private contractDetails: ContractDetails;
    private readonly subscriptions = new Subscription();

    public get mijnFeedbackSettings(): MijnFeedbackSettings {
        return {
            ...this.usabillaFormSettings,
            ...this.feedbackConfiguration,
            type: Variants.MOOD_SIDEBAR
        };
    }

    @HostListener('window:popstate')
    public onPopState(): void {
        if (this.modalComponent) {
            this.modalComponent.cancel();
        }
    }

    @HostListener('window:keydown', ['$event'])
    public onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Tab') {
            this.isKeyboardNavigation = true;
        }
    }

    @HostListener('window:mousedown')
    public onMouseDown(): void {
        this.isKeyboardNavigation = false;
    }

    public ngOnInit(): void {
        this.contentService.addContentItems(content);
        this.isMobileScreenSize();
        this.logoLink = this.getLogoLink();
        this.navigationItems = this.getNavigationItems();
        this.setButtonOrientation();

        this.subscriptions.add(
            this.mijnOverzichtService.getNrOfUnreadMessages().subscribe((unreadMessages) => {
                this.unreadMessagesSize = unreadMessages;

                this.processHeader();
            })
        );

        if (this.tabTitle) {
            this.browserTabTitle.setTitle(this.tabTitle);
        }

        this.subscriptions.add(
            this.mijnTemplateService
                .getContractDetails()
                .pipe(
                    mergeMap((contractDetails) => {
                        return combineLatest([
                            of(contractDetails),
                            this.mijnTemplateService.getHeadlessContent(XC_HEADLESS_CONTENT, contractDetails.is_zakelijk),
                            this.klantBeheerResource.geefIngelogdeKlantDetails().pipe(
                                catchError(() => {
                                    const output: KlantDetails = {
                                        combinedName: ''
                                    } as KlantDetails;

                                    return of(output);
                                })
                            ),
                            this.mijnConditiesService.initializeConditionParameters()
                        ]);
                    })
                )
                .subscribe(([contractDetails, headlessContent, klantDetails, conditionParameters]) => {
                    this.contractDetails = contractDetails;
                    this.pendingActionsCount = +conditionParameters.zelfRegelenDetails?.aantal_ecommerce_authenticaties;
                    if (headlessContent) {
                        this.reload = true;
                        this.contentService.removeContentItems(content);
                        this.contentService.addHeadlessContentItems(headlessContent);
                        this.ngOnChanges({});
                        this.reload = false;
                    }
                    if (this.contractDetails.is_zakelijk) {
                        this.contentService.setContentPrefix(ContentPrefix.BUSINESS);
                        this.companyName = klantDetails.handelsnaam;
                    }
                    this.logoLink = this.getLogoLink();
                    this.navigationItems = this.getNavigationItems();

                    this.userName = klantDetails.combinedName;
                    this.processHeader();
                })
        );

        this.subscriptions.add(
            this.launchdarklyService.getFlagValue(CONVERSATIONAL_CONFIGURATION_NAME, false).subscribe((conversationalEnabled) => {
                this.conversationalEnabled = conversationalEnabled;
            })
        );

        this.mijnOverzichtService.loadNrOfUnreadMessages();

        if (this.updateUnreadMessages$) {
            this.subscriptions.add(
                this.updateUnreadMessages$.subscribe(() => {
                    this.mijnOverzichtService.loadNrOfUnreadMessages();
                })
            );
        }

        if (this.updatePendingActions$) {
            this.subscriptions.add(
                this.updatePendingActions$.subscribe(() => {
                    this.mijnConditiesService.initializeConditionParameters().subscribe((conditionParameters) => {
                        this.pendingActionsCount = +conditionParameters.zelfRegelenDetails?.aantal_ecommerce_authenticaties;
                        this.navigationItems = this.getNavigationItems();
                        this.processHeader();
                    });
                })
            );
        }

        if (!this.updateURL && !this.breadcrumb) {
            this.mijnTemplateService.storeUrl(null, this.title);
        }

        this.setupFeedback();
    }

    public ngAfterViewInit() {
        this.skipLinkTarget = this.document.getElementById('main-content');
        Promise.resolve().then(() => this.focusSkipLink());
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.contractDetails != null) {
            this.logoLink = this.getLogoLink();
            this.navigationItems = this.getNavigationItems();
        }
        if (this.variant === 'secondary') {
            this.renderer.addClass(this.document.body, 'mijn-overzicht-template__body--secondary');
        } else {
            this.renderer.removeClass(this.document.body, 'mijn-overzicht-template__body--secondary');
        }
        this.secondaryClassList = this.setClasses(this.variant);

        if ((changes.updateURL || changes.breadcrumb || changes.title) && changes.title?.currentValue !== '') {
            this.initBreadcrumb(this.updateURL, this.title);
        }
        if (changes.secondaryActionLabel && !this.loading) {
            this.setButtonOrientation();
        }
        if (changes.feedbackConfiguration) {
            this.setupFeedback();
        }
        if (changes.tabTitle) {
            this.browserTabTitle.setTitle(this.tabTitle);
        }
    }

    public setClasses(variant: string): string {
        if (variant === 'secondary') {
            return 'mijn-overzicht-template' + ' ' + 'mijn-overzicht-template--secondary';
        }
        return 'mijn-overzicht-template';
    }

    public onNavigation(item: NavigationItem): void {
        if (item.type === NavigationType.ROUTE_KEY) {
            this.mijnNavigatieService.navigate(MijnRouteKey[item.routeKey]);
        } else if (window.location.href.indexOf(item.link) > 0) {
            window.location.href = item.link;
            window.location.reload();
        }
        setTimeout(() => this.focusSkipLink(), 100);
    }

    public onProfileItemClicked(item: ProfileMenuItem) {
        if (item.routeKey) {
            this.mijnNavigatieService.navigate(MijnRouteKey[item.routeKey]);
        } else if (item.link) {
            window.location.href = item.link;
            window.location.reload();
        }
    }

    private focusSkipLink(): void {
        if (this.isKeyboardNavigation && this.skipLinkElement?.nativeElement) {
            const linkElement = this.skipLinkElement.nativeElement.querySelector('a[data-testid="link"]');
            if (linkElement) {
                linkElement.focus();
            }
        }
    }

    public onLoginLogout(logout: boolean) {
        if (!logout) {
            return;
        }

        this.subscriptions.add(
            this.mijnOverzichtService.getNrOfSendableOrders().subscribe((result) => {
                result === 0 ? this.logout() : this.openSendableOrdersModal();
            })
        );
    }

    private isMobileScreenSize(): boolean {
        const innerWidth = this.windowService.innerWidth();
        this.isMobileSize = innerWidth <= DEFAULT_BREAKPOINT_SMALL.max;
        return this.isMobileSize;
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.renderer.removeClass(this.document.body, 'mijn-overzicht-template__body--secondary');
    }

    public hasActions(): boolean {
        return !!this.primaryActionLabel || !!this.secondaryActionLabel;
    }

    public doPrimaryAction(): void {
        this.primaryActionClicked.emit();
    }

    public doSecondaryAction(): void {
        this.secondaryActionClicked.emit();
    }

    public openSearchPopover() {
        const searchOptions: SearchOptions = { variant: SearchVariant.MODAL, isBusiness: this.contractDetails?.is_zakelijk ?? false };
        const modalOptions = new ModalOptions(searchOptions);
        modalOptions.fullscreen = true;
        modalOptions.showHeader = false;
        modalOptions.classIdentifier = 'search-modal';
        modalOptions.size = ModalSize.LARGE;

        this.modalComponent = this.modalService.open(MijnZoekenComponent, modalOptions);
        this.subscriptions.add(
            this.modalComponent.afterClosed().subscribe((result) => {
                if (result.reason === ModalClosedReason.ACCEPTED) {
                    const resultItem: ResultItem = result.data;
                    if (resultItem.routeKey != null) {
                        this.mijnNavigatieService.navigate(MijnRouteKey[resultItem.routeKey]);
                    } else {
                        this.navigation.external(resultItem.path);
                    }
                }
            })
        );
    }

    public toContact(): void {
        this.navigation.toContact();
    }

    private setupFeedback(): void {
        if (this.feedbackConfiguration) {
            this.subscriptions.add(
                this.mijnOverzichtService.getMijnFeedbackSettings(this.feedbackConfiguration.configurationKey).subscribe((result) => {
                    this.usabillaFormSettings = result;
                })
            );
        }
    }

    private getNavigationItems(): NavigationItem[] {
        return [this.getOverviewItem(), this.getSelfserviceItem(), this.getContactItem(), this.getSearchItem()];
    }

    private getLogoLink(): string {
        return this.getContextualLink('mijn.overzicht.template.header.logo.');
    }

    private getSearchItem(): NavigationItem {
        return {
            ariaLabel: this.contentService.get('mijn.overzicht.template.header.search.title'),
            label: this.contentService.get('mijn.overzicht.template.header.search.title'),
            type: NavigationType.SEARCH
        };
    }

    private getOverviewItem(): NavigationItem {
        const link = this.getContextualLink('mijn.overzicht.template.header.overview.');
        if (this.mijnNavigatieService.isMijnUrl(link)) {
            return {
                label: this.contentService.get('mijn.overzicht.template.header.overview.title'),
                routeKey: this.getContextualLink('mijn.overzicht.template.header.overview.', '.routeKey'),
                type: NavigationType.ROUTE_KEY
            };
        }
        return {
            label: this.contentService.get('mijn.overzicht.template.header.overview.title'),
            link,
            type: NavigationType.LINK_SAME_TAB
        };
    }

    private getSelfserviceItem(): NavigationItem {
        const link = this.getContextualLink('mijn.overzicht.template.header.mijnKlantZelfservice.');
        if (this.mijnNavigatieService.isMijnUrl(link)) {
            return {
                label: this.contentService.get('mijn.overzicht.template.header.mijnKlantZelfservice.title'),
                routeKey: this.getContextualLink('mijn.overzicht.template.header.mijnKlantZelfservice.', '.routeKey'),
                type: NavigationType.ROUTE_KEY,
                badge: this.pendingActionsCount
            };
        }
        return {
            label: this.contentService.get('mijn.overzicht.template.header.mijnKlantZelfservice.title'),
            type: NavigationType.LINK_SAME_TAB,
            badge: this.pendingActionsCount,
            link
        };
    }

    private getContactItem(): NavigationItem {
        const link = this.getContextualLink('mijn.overzicht.template.header.contact.');
        if (this.mijnNavigatieService.isMijnUrl(link)) {
            return {
                label: this.contentService.get('mijn.overzicht.template.header.contact.title'),
                routeKey: this.getContextualLink('mijn.overzicht.template.header.contact.', '.routeKey'),
                type: NavigationType.ROUTE_KEY
            };
        }
        return {
            label: this.contentService.get('mijn.overzicht.template.header.contact.title'),
            link,
            type: NavigationType.LINK_SAME_TAB
        };
    }

    private getTargetLabel(): string {
        if (this.contractDetails?.is_zakelijk) {
            return 'business';
        }
        return 'private';
    }

    private logout(): void {
        const logoutUrl = this.contentService.get(`mijn.overzicht.template.header.logout.${this.getTargetLabel()}.url`);
        this.mijnOverzichtService.clearDynamicBreadcrumbs();
        this.mijnOverzichtService.clearNBASession();
        this.mijnOverzichtService.terminateUserSession().subscribe({
            next: () => {
                this.persistenceService.clearSession('mijn-conversational.chat-session');
                this.navigation.external(logoutUrl);
            }
        });
    }

    private openSendableOrdersModal(): void {
        const modalOptions = new ModalOptions();
        modalOptions.title = this.contentService.get('mijn.overzicht.template.sendableOrdersModal.title');
        modalOptions.size = ModalSize.MEDIUM;

        this.modalComponent = this.modalService.open(SendableOrdersModalComponent, modalOptions);
        this.subscriptions.add(
            this.modalComponent.afterClosed().subscribe((result) => {
                this.handleModalClosedReason(result.reason);
            })
        );
    }

    private handleModalClosedReason(reason: ModalClosedReason): void {
        if (reason === ModalClosedReason.ACCEPTED) {
            this.logout();
        } else if (reason === ModalClosedReason.DECLINED) {
            this.navigation.external(this.contentService.get('mijn.overzicht.template.verzendlijst.url'));
        }
    }

    private initBreadcrumb(url?: string, title?: string): void {
        this.mijnTemplateService.storeUrl(url, title);
        this.dynamicBreadcrumb = this.mijnOverzichtService.getDynamicBreadcrumb() ?? this.breadcrumb;
    }

    private getContextualLink(contentPrefix: string, contentSuffix = '.url'): string {
        return this.contentService.get(`${contentPrefix}${this.getTargetLabel()}${contentSuffix}`);
    }

    private setButtonOrientation(): void {
        if (this.secondaryActionLabel != null && this.isMobileSize) {
            this.buttonsInColumns =
                this.primaryActionLabel.length + this.secondaryActionLabel.length > MAX_BUTTON_TEXTLENGTH_FOR_ROW_LAYOUT;
        }
    }

    private processHeader() {
        this.profileMenu = this.mijnOverzichtService.setupProfileHeader(this.userName, this.companyName, this.unreadMessagesSize);

        this.ngOnChanges({});
    }
}
